import { Form, Spin } from 'antd';
import { SelectionList } from 'components/FilterCombo';
import Select from 'components/Select';
import { SelectItem } from 'components/Select/Select';
import { Viewport } from 'components/ViewportProvider';
import { selectLang } from 'containers/MainLayout/selectors';
import React, { FC, Fragment, lazy, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import { ApplicationRootState } from 'types';
import { commonDimensions } from 'utils/constants';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import media from 'utils/mediaStyle';
import { applyFilter, applySearch, currentPosition } from '../../DistributionLocationPage/actions';
import SearchOutput, { Heading, LocationTabWrapper } from '../../DistributionLocationPage/components/SearchOutput';
import { DEFAULT_CITY } from '../../DistributionLocationPage/constants';
import reducer from '../../DistributionLocationPage/reducer';
import saga from '../../DistributionLocationPage/saga';
import selectDistributionLocationPage from '../../DistributionLocationPage/selectors';
import { ContainerState, LocationSearch } from '../../DistributionLocationPage/type.d';
import { SectionTitle, SectionTitleBox } from './ProductsSource';

const Filters = lazy(() => import('../../DistributionLocationPage/components/Filters'));

export interface IStateProps {
  distributionLocationPage: ContainerState;
  localLang: string;
}

export interface IDispatchProps {
  onFilter: (data: Partial<LocationSearch>) => void;
  onSearch: (data: Partial<LocationSearch>) => void;
  getCurrentPosition: () => void;
}

type Props = IStateProps & IDispatchProps;

const SectionContainer = styled.div`
  background: #ffffff;
  padding: 40px 0px;
  @media (max-width: 991px) {
    padding: 24px 0px;
  }
  ${SectionTitle} {
    justify-content: flex-start;
  }
`;

const AForm = styled(Form)`
  display: flex;
  margin-bottom: 20px;
  flex-wrap: nowrap;
  margin-top: 0px;
  gap: 16px;
  justify-content: space-between;
  ${media.md`
    justify-content: space-around;
    flex-direction: row;
    // margin-block: 20px;
  `}
  & > .ant-form-item {
    // flex: 1;
    margin-bottom: 0;
    margin-inline-end: 0px !important;
    flex-grow: 1;
  }
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  //   min-height: calc(100vh - 75pt);
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  padding: 0 16px;
  ${LocationTabWrapper} {
    max-height: 502px !important;
  }
  ${Heading} {
    display: none;
  }
  @media (max-width: 767px) {
    padding: 0px;
  }
`;

const Content = styled.div`
  padding: 0 16px;
  ${media.md`
    padding: 0;`}
  @media (max-width: 991px) {
    #mapContent {
      display: none !important;
    }
  }
`;

const SectionTitleContainer = styled.div`
  @media (max-width: 991px) {
    padding: 0 16px;
  }
`;

const getInitialSelectionList = (lang: string): SelectionList[] => [
  {
    name: 'city',
    // label: 'Thành phố',
    placeholder: 'Chọn thành phố',
    initialValue: DEFAULT_CITY[lang],
    Component: Select,
    onChange: () => {},
    values: [],
  },
  {
    name: 'district',
    // label: 'Quận',
    placeholder: 'Chọn quận',
    Component: Select,
    onChange: () => {},
    values: [],
  },
  {
    name: 'branch',
    // label: 'Chi nhánh',
    placeholder: 'Chọn cửa hàng',
    Component: Select,
    onChange: () => {},
    values: [],
  },
];

const DistributionLocation: FC<Props> = (props) => {
  const lang = localStorage.getItem('lang') || 'vi';
  const { distributionLocationPage, onFilter, onSearch, localLang } = props;

  const { distribution, search, loading } = distributionLocationPage;

  const [form] = Form.useForm();

  const { data: filterData, locations: filteredLocations } = distribution;

  const selectionList = useMemo(() => {
    const cities = filterData.map((city) => {
      const cityKey = Object.keys(city)[0];
      return {
        value: cityKey,
        label: cityKey,
      };
    });
    /* Init the cities values */
    const initialSelectionList = getInitialSelectionList(localLang);
    initialSelectionList[0].values = cities;
    return initialSelectionList;
  }, [filterData.length, localLang]);

  const getDistrictsByCity = useCallback(
    (city: string): SelectItem[] => {
      const targetCity = filterData.find((item) => item[city]);
      return Object.keys(targetCity?.[city]?.districts || {}).map((district) => ({
        value: district,
        label: district,
      }));
    },
    [filterData.length],
  );

  const getBranchByCityAndDistrict = useCallback(
    (city: string, district: string): SelectItem[] => {
      const targetCity = filterData.find((item) => item[city]);
      return Object.keys(targetCity?.[city]?.districts?.[district]?.branch || {}).map((branch) => ({
        value: branch,
        label: branch,
      }));
    },
    [filterData.length],
  );

  const resetDistrictAndBranch = useCallback(() => {
    const formValue = form?.getFieldValue('distribution-location');
    return form?.setFieldValue('distribution-location', {
      ...formValue,
      district: undefined,
      branch: undefined,
    });
  }, []);

  const resetBranch = useCallback(() => {
    const formValue = form?.getFieldValue('distribution-location');
    return form?.setFieldValue('distribution-location', {
      ...formValue,
      branch: undefined,
    });
  }, []);

  return (
    <Fragment>
      <Viewport.Consumer>
        {({ width }) => (
          <SectionContainer>
            <MainContainer className="container-padding">
              <SectionTitleContainer>
                <SectionTitleBox>
                  {lang === 'vi' ? (
                    <>
                      <SectionTitle highLight>
                        Địa điểm phân phối
                        <SectionTitle>&nbsp;sản phẩm Kamereo gần bạn</SectionTitle>
                      </SectionTitle>
                    </>
                  ) : (
                    <>
                      <SectionTitle highLight>
                        Kamereo Distribution Locations
                        <SectionTitle>&nbsp;Near You</SectionTitle>
                      </SectionTitle>
                    </>
                  )}
                </SectionTitleBox>
              </SectionTitleContainer>
              <Content>
                <SearchOutput
                  form={form}
                  filteredLocations={filteredLocations}
                  onSearch={onSearch}
                  spinning={search.currentLocationLoading || loading}
                  lang={localLang}
                >
                  <Spin spinning={search.currentLocationLoading || loading}>
                    <AForm
                      name="distribution-location"
                      layout={'inline'}
                      form={form}
                      className="filter-form"
                      onFinish={() => {}}
                      onFinishFailed={() => {}}
                      size="large"
                    >
                      {filterData.length ? (
                        <Filters
                          form={form}
                          selectionList={selectionList}
                          prefixNames={['distribution-location']}
                          getDistrictsByCity={getDistrictsByCity}
                          getBranchByCityAndDistrict={getBranchByCityAndDistrict}
                          resetDistrictAndBranch={resetDistrictAndBranch}
                          resetBranch={resetBranch}
                          onFilter={onFilter}
                          lang={localLang}
                        />
                      ) : null}
                    </AForm>
                  </Spin>
                </SearchOutput>
              </Content>
            </MainContainer>
          </SectionContainer>
        )}
      </Viewport.Consumer>
    </Fragment>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  distributionLocationPage: selectDistributionLocationPage(),
  localLang: selectLang(),
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onFilter: (data: Partial<LocationSearch>) => dispatch(applyFilter(data)),
    onSearch: (data: Partial<LocationSearch>) => dispatch(applySearch(data)),
    getCurrentPosition: () => dispatch(currentPosition.request()),
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer({ key: 'distributionLocationPage', reducer: reducer });

const withSaga = injectSaga({ key: 'distributionLocationPage', saga: saga });

export default compose(withReducer, withSaga, withConnect)(DistributionLocation);
